import gql from 'graphql-tag';
import { type DocumentNode } from 'graphql';
import type { ReaderSelection, ReaderFragment, GraphQLTaggedNode } from 'relay-runtime';

/**
 * Normalizes GraphQL errors to ensure graphQLErrors is always an array
 * This prevents "error.graphQLErrors.every is not a function" errors
 */
function normalizeGraphQLError(error: any): never {
	if (error && !Array.isArray(error.graphQLErrors)) {
		error.graphQLErrors = error.graphQLErrors ? [error.graphQLErrors] : [];
	}
	throw error;
}

/**
 * Safe wrapper around gql() that normalizes errors
 */
function safeGql(query: string): DocumentNode {
	try {
		return gql(query);
	} catch (error) {
		normalizeGraphQLError(error);
	}
}

/**
 * Checks if a string value is likely a GraphQL enum value.
 * Enum values are typically all uppercase with underscores (e.g., ATLAS_DOC_FORMAT)
 *
 * NOTE: This is a heuristic because Relay's ReaderFragment doesn't include schema
 * type metadata to distinguish enum values from string literals at runtime.
 * Both are stored as plain strings in the `value` field of Literal arguments.
 *
 * Limitation: This won't correctly handle:
 * - Lowercase enum values (e.g., enum Status { active, inactive })
 * - String literals that look like enums (e.g., "CONSTANT_VALUE")
 *
 * For queries/mutations, use params.text instead which has correct formatting.
 */
function isLikelyEnum(value: string): boolean {
	// Check if the string matches enum naming conventions:
	// - Starts with uppercase letter
	// - Contains only uppercase letters, numbers, and underscores
	// - Has at least one uppercase letter
	return /^[A-Z][A-Z0-9_]*$/.test(value);
}

export function toDocumentNode(graphqlTaggedNode: GraphQLTaggedNode | DocumentNode): DocumentNode {
	if (typeof graphqlTaggedNode === 'string') {
		// For backwards-compatibility only; that's why `string` is not specified as an accepted argument type
		return safeGql(graphqlTaggedNode);
	}
	if ('kind' in graphqlTaggedNode && 'definitions' in graphqlTaggedNode) {
		return graphqlTaggedNode;
	}

	if ('kind' in graphqlTaggedNode && graphqlTaggedNode.kind === 'Fragment') {
		return fragmentToDocumentNode(graphqlTaggedNode);
	}

	if (!('params' in graphqlTaggedNode)) {
		throw new Error(
			'[Relay Migration] unsupported tagged node provided. Please make sure you generated Relay artifacts for your operation',
		);
	}

	const operationText = graphqlTaggedNode.params.text;

	if (!operationText) {
		throw new Error(
			'[Relay Migration] The provided `ConcreteRequest` did not specify `params.text` properties that is required for Apollo deprecation. Make sure you keep the `persiste: { includeQueryText: true }` Relay compiler configuration option ',
		);
	}

	return safeGql(graphqlTaggedNode.params.text);
}

function fragmentToDocumentNode(readerFragment: ReaderFragment) {
	const selections = readerFragment.selections;

	// Helper to format a single value for GraphQL
	function formatValue(v: any): string {
		if (typeof v === 'string') {
			// Check if this is an enum value (unquoted) or a string literal (quoted)
			if (isLikelyEnum(v)) {
				return v; // Enum values are not quoted
			}
			return `"${v}"`;
		}
		return String(v);
	}

	// Format arguments for a field
	function formatArguments(args: readonly any[]): string {
		if (!args || args.length === 0) {
			return '';
		}

		const formattedArgs = args.map((arg) => {
			let value: string;
			if (arg.kind === 'Variable') {
				// Variable reference: $variableName
				value = `$${arg.variableName}`;
			} else if (arg.kind === 'Literal') {
				// Literal value: format based on type
				if (typeof arg.value === 'string') {
					// Check if this is an enum value (unquoted) or a string literal (quoted)
					if (isLikelyEnum(arg.value)) {
						value = arg.value; // Enum values are not quoted
					} else {
						value = `"${arg.value}"`;
					}
				} else if (typeof arg.value === 'number' || typeof arg.value === 'boolean') {
					value = String(arg.value);
				} else if (arg.value === null) {
					value = 'null';
				} else if (Array.isArray(arg.value)) {
					const arrayItems = arg.value.map(formatValue).join(', ');
					value = `[${arrayItems}]`;
				} else if (typeof arg.value === 'object') {
					// Object literal - convert to GraphQL input object syntax
					const fields = Object.entries(arg.value)
						.map(([k, v]: [string, any]) => {
							const formattedValue = formatValue(v);
							return `${k}: ${formattedValue}`;
						})
						.join(', ');
					value = `{${fields}}`;
				} else {
					value = String(arg.value);
				}
			} else {
				value = String(arg.value);
			}
			return `${arg.name}: ${value}`;
		});

		return `(${formattedArgs.join(', ')})`;
	}

	function selectionToString(selection: ReaderSelection): string {
		const sel = selection as any;
		switch (sel.kind) {
			case 'ScalarField': {
				// Handles: id, name, email
				// Added: Support for aliases (e.g., userName: name)
				const alias = sel.alias ? `${sel.alias}: ` : '';
				const args = formatArguments(sel.args || []);
				return `${alias}${sel.name}${args}`;
			}

			case 'LinkedField': {
				// Handles: nested objects like profile { avatar bio }
				// Added: Support for arguments and aliases
				const alias = sel.alias ? `${sel.alias}: ` : '';
				const args = formatArguments(sel.args || []);
				const nestedSelections = sel.selections.map(selectionToString).join(' ');
				return `${alias}${sel.name}${args}{${nestedSelections}}`;
			}

			case 'InlineFragment': {
				// Handles: ... on TypeName { fields }
				// Why: Used for union types and interfaces to select fields based on concrete type
				// Example: ... on InlineComment { markerRef }
				const typeCondition = sel.type ? ` on ${sel.type}` : '';
				const nestedSelections = sel.selections.map(selectionToString).join(' ');
				return `...${typeCondition} {${nestedSelections}}`;
			}

			case 'FragmentSpread': {
				// Handles: ...FragmentName
				// Why: Used to include other fragment definitions
				// Example: ...UserProfileFragment
				// Note: Fragment spreads with arguments (@arguments directive) are Relay-specific
				// and need to be converted to inline the fragment instead
				return `...${sel.name}`;
			}

			case 'Condition': {
				// Handles: @include(if: $var) or @skip(if: $var)
				// Why: Conditional field inclusion based on variables
				// Example: field @include(if: $showField)
				const condition = sel.condition;
				const passingValue = sel.passingValue;
				const directiveName = passingValue ? '@include' : '@skip';
				const directiveStr = `${directiveName}(if: $${condition})`;

				// Apply the directive to each nested selection
				const nestedSelections = sel.selections
					.map((s: any) => {
						const fieldStr = selectionToString(s);
						// Add directive after the field but before any nested braces or directives
						// For simple fields, just append the directive
						// For complex fields with selections, insert before the opening brace
						if (fieldStr.includes('{')) {
							const braceIndex = fieldStr.indexOf('{');
							const beforeBrace = fieldStr.slice(0, braceIndex);
							const afterBrace = fieldStr.slice(braceIndex);
							return `${beforeBrace} ${directiveStr} ${afterBrace}`;
						} else {
							return `${fieldStr} ${directiveStr}`;
						}
					})
					.join(' ');
				return nestedSelections;
			}

			case 'Defer': {
				// Handles: @defer directive for deferred fragments
				// Why: Progressive loading of fragments
				// Example: ... @defer { slowField }
				const labelStr =
					'label' in selection && selection.label ? `(label: "${selection.label}")` : '';
				const nestedSelections =
					'selections' in selection && selection.selections
						? selection.selections.map(selectionToString).join(' ')
						: '';
				const deferDirective = `... @defer${labelStr}`;
				return `${deferDirective} {${nestedSelections}}`;
			}

			case 'ModuleImport': {
				// Handles: @module and @match directives
				// Why: Used for code-splitting and dynamic imports in Relay
				// Note: Apollo doesn't have direct equivalent, may need custom handling
				// This is rarely used and Apollo doesn't support it directly
				const nestedSelections =
					'selections' in selection && selection.selections
						? selection.selections.map(selectionToString).join(' ')
						: '';
				const docName =
					'documentName' in selection && selection.documentName
						? selection.documentName
						: 'unknown';
				return nestedSelections || `# ModuleImport: ${docName}`;
			}

			case 'TypeDiscriminator': {
				// Handles: __typename field for type discrimination
				// Why: Used by Relay for abstract types (unions/interfaces)
				// Apollo uses __typename extensively, so this should be preserved
				return '__typename';
			}

			default: {
				// Catch any other selection kinds that might exist
				// This ensures we don't silently ignore new Relay features
				throw new Error(
					`[Relay Migration] unimplemented fragment selection kind: ${(selection as any).kind}`,
				);
			}
		}
	}

	// Note: Fragments in GraphQL don't support variable definitions
	// Variables are declared at the operation (query/mutation) level
	// and are available to all fragments within that operation.
	const selectionsStr = selections.map(selectionToString).join(' ');

	// Ensure proper spacing in GraphQL syntax
	const fragmentDef = `fragment ${readerFragment.name} on ${readerFragment.type} {
		${selectionsStr}
	}`;

	try {
		return safeGql(fragmentDef);
	} catch (error) {
		// Add context about the generated GraphQL for debugging
		const contextError: any = error;
		if (contextError) {
			const originalMessage = contextError.message;
			const errorPrefix = '[Relay Migration] Failed to parse generated GraphQL fragment: ';
			const generatedGraphQL = `\nGenerated GraphQL:\n${fragmentDef}`;
			contextError.message = errorPrefix + originalMessage + generatedGraphQL;
		}
		throw error;
	}
}
